import {
  Box,
  Chip,
  CircularProgress,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useEffect, useRef, useState } from "react";
import { axiosInstance } from "../../../utils/networking";
import dayjs from "dayjs";
import { BROADCAST_TYPES } from "./broadcastTypes";
import StatusChip from "./BroadcastStatusChip";
import BroadcastDetailDrawer from "./BroadcastDetailDrawer";

const columns: GridColDef[] = [
  {
    field: "createdAt",
    headerName: "Date",
    width: 160,
    renderCell: (params) =>
      params.value ? dayjs(params.value).format("MMM D, YYYY h:mm A") : "—",
  },
  {
    field: "broadcastType",
    headerName: "Type",
    width: 160,
    renderCell: (params) =>
      BROADCAST_TYPES.find((t) => t.id === params.value)?.label || params.value,
  },
  { field: "subject", headerName: "Subject", flex: 1, minWidth: 200 },
  { field: "fromBFC", headerName: "Sent By", width: 160 },
  {
    field: "deliveredCount",
    headerName: "Delivered",
    width: 100,
    align: "right",
    headerAlign: "right",
    renderCell: (params) => (params.value || 0).toLocaleString(),
  },
  {
    field: "errorCount",
    headerName: "Errors",
    width: 90,
    align: "right",
    headerAlign: "right",
    renderCell: (params) => {
      const count = params.value || 0;
      if (count === 0) return "0";
      return (
        <Chip
          label={count.toLocaleString()}
          size="small"
          color="error"
          variant="outlined"
        />
      );
    },
  },
  {
    field: "recipientCount",
    headerName: "Total",
    width: 90,
    align: "right",
    headerAlign: "right",
    renderCell: (params) =>
      params.value == null ? "—" : params.value.toLocaleString(),
  },
  {
    field: "status",
    headerName: "Status",
    width: 110,
    renderCell: (params) => <StatusChip status={params.value} />,
  },
];

const BroadcastHistory = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchBroadcasts = async () => {
    try {
      const res = await axiosInstance.get("/broadcast");
      const broadcasts = (res.data.broadcasts || []).map((b: any) => ({
        ...b,
        id: b._id,
      }));
      setRows(broadcasts);
      return broadcasts;
    } catch (err) {
      console.error("Failed to fetch broadcasts:", err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBroadcasts().then((broadcasts) => {
      const hasSending = broadcasts.some(
        (b: any) => b.status === "queued" || b.status === "sending",
      );
      if (hasSending) {
        pollRef.current = setInterval(async () => {
          const updated = await fetchBroadcasts();
          const stillSending = updated.some(
            (b: any) => b.status === "queued" || b.status === "sending",
          );
          if (!stillSending && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }, 5000);
      }
    });

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography color="text.secondary">
          No broadcasts sent yet. Create your first broadcast in the "New Broadcast" tab.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 500 }}>
      <DataGrid
        rows={rows}
        columns={columns}
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
        onRowClick={(params) => {
          setSelected(params.row);
          setDrawerOpen(true);
        }}
        sx={{
          "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f5f5f5" },
          "& .MuiDataGrid-row": { cursor: "pointer" },
          "& .MuiDataGrid-row:hover": { backgroundColor: "#fce4ec" },
        }}
      />

      <BroadcastDetailDrawer
        key={selected?._id}
        open={drawerOpen}
        broadcast={selected}
        onClose={() => setDrawerOpen(false)}
        onExited={() => setSelected(null)}
      />
    </Box>
  );
};

export default BroadcastHistory;
